/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useLayoutEffect, useState } from 'react';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	ContentComponentContextProvider,
	type ContentComponentProps,
} from '../../hooks/useContentComponentContext';
import { GlobalWrapper } from '../GlobalWrapper/GlobalWrapper';
import { SessionUIComponent } from '../SessionUI/SessionUIComponent';
import { configItemUsesRovo } from '../SessionUI/utils/checkMigrationStatus';

import { ContentComponent } from './ContentComponent';

const useSharedPluginStateNoDebounce = (api: ContentComponentProps['api']) => {
	const [state, setState] = useState(
		api?.aiExperience?.sharedState.currentState()?.aiDecorationExperiencePluginState
			.modalMountedTimeStamp,
	);
	useLayoutEffect(() => {
		const unsub = api?.aiExperience?.sharedState.onChange(
			({ prevSharedState, nextSharedState }) => {
				if (
					prevSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp !==
					nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp
				) {
					setState(nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp);
				}
			},
		);

		return () => {
			unsub?.();
		};
	}, [api]);

	return state;
};

export const ContentComponentWrapper = (
	props: Omit<
		ContentComponentProps,
		'isRovoLoading' | 'setIsRovoLoading' | 'cancelRovoPrompt' | 'setCancelRovoPrompt'
	>,
) => {
	const modalMountedTimeStamp = useSharedPluginStateNoDebounce(props.api);
	const { targetId, isDisplayingChanges, isRovoAIFC } = useSharedPluginStateWithSelector(
		props.api,
		['aiExperience', 'trackChanges'],
		(state) => ({
			targetId: state.aiExperienceState?.aiDecorationExperiencePluginState.targetId,
			isDisplayingChanges: state.trackChangesState?.isDisplayingChanges,
			isRovoAIFC: configItemUsesRovo(
				state.aiExperienceState?.aiDecorationExperiencePluginState.configItem?.key,
			),
		}),
	);

	/**
	 * This is to detect the state of the Rovo prompt and loading screens
	 * from RovoInlineChatPrompt used in RovoPromptScreenWithLogic,
	 * as it does not respect the state value from XState.
	 */
	const [isRovoLoading, setIsRovoLoading] = useState(false);
	/**
	 * Stores Rovo's cancel function from onSubmit callback so ExperienceApplication's
	 * ESC handler can call it. Needed because editor loses focus when disabled during
	 * streaming, breaking normal ESC handling.
	 */
	const [cancelRovoPrompt, setCancelRovoPrompt] = useState<() => void>();

	/* reset the component when start AI again, so that the error boundary will also be reset */
	return (
		<GlobalWrapper>
			<ContentComponentContextProvider
				value={{
					...props,
					isRovoLoading,
					setIsRovoLoading,
					targetId,
					isDisplayingChanges,
					setCancelRovoPrompt,
					cancelRovoPrompt,
				}}
			>
				{(!fg('platform_editor_ai_migrate_from_xstate') || !isRovoAIFC) && (
					<ContentComponent key={modalMountedTimeStamp} />
				)}

				<SessionUIComponent
					api={props.api}
					providerFactory={props.providerFactory}
					appearance={props.appearance}
				/>
			</ContentComponentContextProvider>
		</GlobalWrapper>
	);
};
